import { DROP_STATUS_LABELS, type DropStatus } from './cable.ts';
import { type CapacityBand, CAPACITY_BAND_LABELS } from './capacity.ts';
import { PORT_STATE_LABELS, PORT_STATE_ORDER, type PortConnectionState } from './port-state.ts';
import {
  READING_RESULT_LABELS,
  READING_RESULTS,
  type ReadingResult,
} from './schemas/cable-reading.ts';
import { DEFAULT_ITEM_STATUS, ITEM_STATUS_LABELS, type ItemStatus } from './schemas/item.ts';
import {
  PORT_STATE_LABELS as PANEL_POSITION_LABELS,
  type PortStateName as PanelPosition,
} from './wiring-colors.ts';

// ROADMAP P6: the one way the app says what state a thing is in. Every state
// worth showing — a port's, a panel position's, a device's, a run's, a
// tester's verdict, how full something is, what a queued write is doing —
// resolves to a mark: a colour token and a shape.
//
// Pure, and with no colour of its own, exactly as `capacity.ts` is: a mark
// names a CSS custom property and the app resolves it, so one state is one
// colour in the light theme and the dark theme without either being written
// down twice.
//
// The shape is not decoration. A drawing printed in grey, or read by someone
// who cannot separate the hues, still has to say which port is patched and
// which reading failed, so no state is told apart by its colour alone.

/**
 * How a mark is drawn, beside its colour: filled, an empty outline, dashed
 * (present but not carried through), or hatched (a fault).
 */
export type StatusShape = 'filled' | 'outline' | 'dashed' | 'striped';

export interface StatusMark {
  /** Stable key for a list of marks: the state's own name. */
  readonly id: string;
  /** What the state is called, in the words the rest of the app uses. */
  readonly label: string;
  /**
   * The CSS custom property that colours the mark, without its leading `--`.
   * A state on a scale of its own names that scale's token (`capacity-busy`,
   * `port-complete`); a state with no scale names one of the five tones
   * (`status-neutral`, `status-info`, `status-good`, `status-warn`,
   * `status-bad`). All are defined in `src/styles/tokens.css`, for both
   * themes, and nowhere else.
   */
  readonly token: string;
  readonly shape: StatusShape;
}

/** The five tones a state with no scale of its own is drawn in. */
export const STATUS_TONES = {
  neutral: 'status-neutral',
  info: 'status-info',
  good: 'status-good',
  warn: 'status-warn',
  bad: 'status-bad',
} as const;

// --- Port connection state (A6) ---------------------------------------------

/**
 * Complete and incomplete are filled — the link is there, and the colour says
 * whether it reaches an active device. A landed run is dashed: present, but
 * not carried through. A free port is an empty outline.
 */
const PORT_STATE_MARKS: Readonly<Record<PortConnectionState, StatusMark>> = {
  complete: {
    id: 'complete',
    label: PORT_STATE_LABELS.complete,
    token: 'port-complete',
    shape: 'filled',
  },
  incomplete: {
    id: 'incomplete',
    label: PORT_STATE_LABELS.incomplete,
    token: 'port-incomplete',
    shape: 'filled',
  },
  landed: { id: 'landed', label: PORT_STATE_LABELS.landed, token: 'port-landed', shape: 'dashed' },
  none: { id: 'none', label: PORT_STATE_LABELS.none, token: 'port-none', shape: 'outline' },
};

/** The port-state key, less any state this device cannot be in. */
export function portStateMarks(omit: readonly PortConnectionState[] = []): StatusMark[] {
  return PORT_STATE_ORDER.filter((state) => !omit.includes(state)).map(
    (state) => PORT_STATE_MARKS[state],
  );
}

// --- Panel positions (I3, I6) -----------------------------------------------

/**
 * A panel position's four names, painted in the device-port colours they
 * correspond to — patched through is a complete link, one side only an
 * incomplete one — so the schedule and the live faceplate agree.
 */
export const PANEL_POSITION_MARKS: Readonly<Record<PanelPosition, StatusMark>> = {
  free: { id: 'free', label: PANEL_POSITION_LABELS.free, token: 'port-none', shape: 'outline' },
  linked: {
    id: 'linked',
    label: PANEL_POSITION_LABELS.linked,
    token: 'port-complete',
    shape: 'filled',
  },
  landed: {
    id: 'landed',
    label: PANEL_POSITION_LABELS.landed,
    token: 'port-landed',
    shape: 'dashed',
  },
  half: {
    id: 'half',
    label: PANEL_POSITION_LABELS.half,
    token: 'port-incomplete',
    shape: 'filled',
  },
};

// --- Capacity bands (D11) ---------------------------------------------------

/**
 * How full a thing is. The colour carries the order, cool to hot, so the shape
 * only has to separate the two bands that are not a working level: an empty
 * outline for nothing in it, and hatching for a thing past what it is rated
 * for, which is a fault rather than a busier shade of full.
 */
const CAPACITY_MARKS: Readonly<Record<CapacityBand, StatusMark>> = {
  unknown: {
    id: 'unknown',
    label: CAPACITY_BAND_LABELS.unknown,
    token: 'capacity-empty',
    shape: 'outline',
  },
  empty: {
    id: 'empty',
    label: CAPACITY_BAND_LABELS.empty,
    token: 'capacity-empty',
    shape: 'outline',
  },
  light: {
    id: 'light',
    label: CAPACITY_BAND_LABELS.light,
    token: 'capacity-light',
    shape: 'filled',
  },
  busy: { id: 'busy', label: CAPACITY_BAND_LABELS.busy, token: 'capacity-busy', shape: 'filled' },
  full: { id: 'full', label: CAPACITY_BAND_LABELS.full, token: 'capacity-full', shape: 'filled' },
  over: { id: 'over', label: CAPACITY_BAND_LABELS.over, token: 'capacity-over', shape: 'striped' },
};

/**
 * The capacity key. `unknown` is left out, as it is from `CAPACITY_LEGEND`:
 * it is the absence of a figure rather than a level of fullness, and a key
 * that listed it would invite someone to read grey as a measurement.
 */
export const CAPACITY_LEGEND_MARKS: readonly StatusMark[] = (
  ['empty', 'light', 'busy', 'full', 'over'] satisfies readonly CapacityBand[]
).map((band) => CAPACITY_MARKS[band]);

// --- Item status ------------------------------------------------------------

const ITEM_STATUS_MARKS: Readonly<Record<ItemStatus, StatusMark>> = {
  planned: {
    id: 'planned',
    label: ITEM_STATUS_LABELS.planned,
    token: STATUS_TONES.info,
    shape: 'outline',
  },
  installed: {
    id: 'installed',
    label: ITEM_STATUS_LABELS.installed,
    token: STATUS_TONES.good,
    shape: 'filled',
  },
  spare: {
    id: 'spare',
    label: ITEM_STATUS_LABELS.spare,
    token: STATUS_TONES.neutral,
    shape: 'outline',
  },
  retired: {
    id: 'retired',
    label: ITEM_STATUS_LABELS.retired,
    token: STATUS_TONES.neutral,
    shape: 'dashed',
  },
};

/**
 * The mark beside a device's name: its status when that is not the norm, and
 * nothing when it is. The same rule `itemStatusTag` has always applied — an
 * installed device wears no chip — now carrying a colour and a shape as well
 * as the word.
 */
export function itemStatusMark(status: ItemStatus): StatusMark | null {
  return status === DEFAULT_ITEM_STATUS ? null : ITEM_STATUS_MARKS[status];
}

// --- Run status -------------------------------------------------------------

const DROP_STATUS_MARKS: Readonly<Record<DropStatus, StatusMark>> = {
  active: {
    id: 'active',
    label: DROP_STATUS_LABELS.active,
    token: STATUS_TONES.good,
    shape: 'filled',
  },
  spare: {
    id: 'spare',
    label: DROP_STATUS_LABELS.spare,
    token: STATUS_TONES.neutral,
    shape: 'outline',
  },
};

/** A run's state: pulled and carrying links, or pulled and held in reserve. */
export function dropStatusMark(status: DropStatus): StatusMark {
  return DROP_STATUS_MARKS[status];
}

// --- Tester verdicts (I9) ---------------------------------------------------

const READING_RESULT_MARKS: Readonly<Record<ReadingResult, StatusMark>> = {
  pass: {
    id: 'pass',
    label: READING_RESULT_LABELS.pass,
    token: STATUS_TONES.good,
    shape: 'filled',
  },
  fail: { id: 'fail', label: READING_RESULT_LABELS.fail, token: STATUS_TONES.bad, shape: 'filled' },
  // Inside the tester's limits but not clear of them: hatched, so "look at
  // this one" survives a grey print.
  marginal: {
    id: 'marginal',
    label: READING_RESULT_LABELS.marginal,
    token: STATUS_TONES.warn,
    shape: 'striped',
  },
};

export function readingResultMark(result: ReadingResult): StatusMark {
  return READING_RESULT_MARKS[result];
}

/** The verdict key, in the order a tester's results are worth reading. */
export const READING_RESULT_LEGEND: readonly StatusMark[] = READING_RESULTS.map(
  (result) => READING_RESULT_MARKS[result],
);
